import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { Combobox } from "@/components/ui/combobox";

const DISTRICT_COUNCILS = [
  "DC 1M", "DC 3", "DC 4", "DC 5", "DC 6", "DC 7", "DC 9", "DC 11", "DC 16",
  "DC 21", "DC 30", "DC 35", "DC 51", "DC 57", "DC 58", "DC 77", "DC 82",
  "DC 88", "DC 91", "DC 711",
].map((name) => ({ value: name, label: name }));

const code = `import { Combobox } from "@/components/ui/combobox";

const options = districtCouncils.map((dc) => ({
  value: String(dc.id),
  label: dc.name,
}));

<Combobox
  options={options}
  value={selectedId}
  onChange={setSelectedId}
  placeholder="Select a District Council…"
  searchPlaceholder="Type to filter…"
  emptyMessage="No District Council matches."
  triggerLabel="Change which organization you are viewing"
/>`;

export function ComboboxSection() {
  const [value, setValue] = React.useState<string>("DC 16");
  const [empty, setEmpty] = React.useState<string>("");

  return (
    <SectionShell
      id="combobox"
      title="Combobox"
      description={
        <>
          A single-select dropdown you can type into to filter. The list is
          keyboard navigable (<code>↑</code> / <code>↓</code> / <code>Enter</code>{" "}
          / <code>Esc</code>), the selected option is checked, and the panel
          matches the trigger&apos;s width. Built on Radix Popover, so focus
          management and outside-click dismissal come for free.
        </>
      }
      whenToUse={
        <ul>
          <li>Selecting from a long list — a few dozen options or more.</li>
          <li>Picking an organization, member, or other named record.</li>
          <li>Anywhere scanning a plain <code>Select</code> would be work.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>For a handful of options — use <code>Select</code>.</li>
          <li>For actions rather than values — use <code>DropdownMenu</code>.</li>
          <li>For choosing several things at once — use checkboxes.</li>
        </ul>
      }
      preview={
        <div className="flex flex-wrap items-start gap-8">
          <div className="flex w-64 flex-col gap-2">
            <Combobox
              options={DISTRICT_COUNCILS}
              value={value}
              onChange={setValue}
              searchPlaceholder="Filter District Councils…"
              triggerLabel="District Council"
            />
            <span className="text-xs text-ink-muted">With a selection</span>
          </div>
          <div className="flex w-64 flex-col gap-2">
            <Combobox
              options={DISTRICT_COUNCILS}
              value={empty}
              onChange={setEmpty}
              placeholder="Select a District Council…"
              searchPlaceholder="Filter District Councils…"
              triggerLabel="District Council"
            />
            <span className="text-xs text-ink-muted">Empty state</span>
          </div>
          <div className="flex w-64 flex-col gap-2">
            <Combobox
              options={DISTRICT_COUNCILS}
              value="DC 16"
              onChange={() => {}}
              disabled
              triggerLabel="District Council"
            />
            <span className="text-xs text-ink-muted">Disabled</span>
          </div>
        </div>
      }
      code={code}
    />
  );
}
